import Modal from "@/Components/Modal";
import TextInput from "@/Components/TextInput";
import InputLabel from "@/Components/InputLabel";
import PrimaryButton from "@/Components/PrimaryButton";
import SecondaryButton from "@/Components/SecondaryButton";
import {useState} from "react";
import {Subtitle} from "@/models/Subtitle";

type SplitLinesModalProps = {
    show: boolean;
    onClose: () => void;
    items: Subtitle[];
    onApply: (updates: { index: number; item: Subtitle }[]) => void;
}

function splitLine(line: string, maxCharsPerLine: number): string[] {
    if (line.length <= maxCharsPerLine) return [line];

    const words = line.split(/\s+/);
    const result: string[] = [];
    let currentLine = '';

    for (const word of words) {
        const candidate = currentLine ? currentLine + ' ' + word : word;
        if (candidate.length > maxCharsPerLine && currentLine) {
            result.push(currentLine);
            currentLine = word;
        } else {
            currentLine = candidate;
        }
    }
    if (currentLine) {
        result.push(currentLine);
    }

    return result;
}

function splitText(text: string, maxCharsPerLine: number): string {
    const existingLines = text.split('\n');
    return existingLines.flatMap(line => splitLine(line, maxCharsPerLine)).join('\n');
}

const STORAGE_KEY = 'splitLines.maxChars';

export default function SplitLinesModal({show, onClose, items, onApply}: SplitLinesModalProps) {
    const [maxChars, setMaxChars] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) return Number(saved);
        } catch {}
        return 42;
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const updates: { index: number; item: Subtitle }[] = [];

        items.forEach((item, index) => {
            if (item.deleted) return;

            const newText = splitText(item.text, maxChars);
            if (newText !== item.text) {
                updates.push({index, item: new Subtitle({...item, text: newText})});
            }
        });

        localStorage.setItem(STORAGE_KEY, String(maxChars));
        onApply(updates);
    };

    return (
        <Modal maxWidth='sm' show={show} onClose={onClose}>
            <div className="p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">
                    Split Lines
                </h3>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <InputLabel htmlFor="maxChars" value="Max characters per line" className="mb-2"/>
                        <TextInput
                            id="maxChars"
                            type="number"
                            min={10}
                            max={200}
                            value={maxChars}
                            onChange={(e) => setMaxChars(Number(e.target.value))}
                            className="w-full"
                            isFocused
                        />
                    </div>

                    <div className="flex justify-end">
                        <SecondaryButton type="button" className="me-2" onClick={onClose}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton type="submit">
                            Apply
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
